import * as Sentry from '@sentry/react';

// direct imports help with tree-shaking to keep bundle size down
import _transform from 'lodash/transform'
import _isArray from 'lodash/isArray'
import _isObject from 'lodash/isObject'
import _snakeCase from 'lodash/snakeCase'
import _camelCase from 'lodash/camelCase'

enum HttpMethod {
  GET='GET',
  PUT='PUT',
  PATCH='PATCH',
  POST='POST',
  DELETE='DELETE',
}

interface FetcherOptions {
  method?: keyof typeof HttpMethod
  body?: { [key:string]: any } | string
  queryParams?: { [key:string]: any }
  headers?: { [key:string]: any }
  clientSlugPath?: string
}

type BaseJsonFetcherOptions = {
  method?: keyof typeof HttpMethod;
  body?: string;
  clientSlugPath?: string;
};

interface Options {
  method?: keyof typeof HttpMethod
  body?: string
  headers?: { [key:string]: any }
  credentials?: 'include' | 'omit' | 'same-origin'
}

interface FetcherData<T> {
  data?: T
  errors?: ApiErrorInfo
  error?: string
}

export interface ApiErrorInfo {
  [key: string]: string[];
}

export class ApiError extends Error {
  status: number;
  info: ApiErrorInfo;

  constructor(message: string, status: number, info: ApiErrorInfo) {
    super(message);
    this.status = status;
    this.info = info;
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  // Helper for toast - get general error message
  getDisplayMessage(): string {
    if (this.info.base) return this.info.base[0];
    const firstField = Object.keys(this.info)[0];
    if (firstField) return this.info[firstField][0];
    return this.message || 'An unexpected error occurred';
  }
}

let cachedSlug: string | null = null;

const getSlug = () => {
  if (cachedSlug) return cachedSlug;
  
  // Widget context - only check shadow host data attribute
  if (import.meta.env.VITE_WIDGET === 'true') {
    console.log('widget context')
    if (typeof window === 'undefined') return '/null';
    
    const widgetHost = document.getElementById('myclinically-widget-host');
    console.log(widgetHost?.dataset, 'widgetHost')
    if (widgetHost?.dataset.clientSlug) {
      cachedSlug = `/${widgetHost.dataset.clientSlug}`;
      return cachedSlug;
    }
    return '/null';
  }

  // Next.js app context - get slug from hostname
  if (typeof window === 'undefined') return '';
  cachedSlug = `/${window.location.host.split('.')[0]}`;
  return cachedSlug;
};

export const slug = getSlug();

export const apiUrl = (clientSlug:string) => {
  const currentSlug = getSlug();
  switch(import.meta.env.VITE_APP_ENV){
    case 'sandbox':
      return`https://${currentSlug}.sandbox.myclinically.com/nemo${clientSlug}`
    case 'production':
      return `https://${currentSlug}.myclinically.com/nemo${clientSlug}`
    default:
      return `https://${currentSlug}.local.myclinically.com:3001/nemo${clientSlug}`
  }
}

const mergeOptions = (options:Options):Options => {
  return {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  }
}

export interface JsonResponse<T> {
  status: number
  // Undefined when the response was not JSON. The CloudFront distribution and
  // the WAF in front of the API answer with HTML — a rate-limit block page is
  // the likeliest thing a patient hits here — so the status is the only part a
  // caller can rely on.
  body: T | undefined
}

// The /auth routes answer a refusal with a 4xx and a body rather than a network
// failure, so a caller that has to tell one refusal from another (wrong
// password vs. locked out) can only do it by status — the prose is deliberately
// identical between them.
export const baseJsonFetcherWithStatus = <T>(
  path: string,
  options: BaseJsonFetcherOptions = {},
): Promise<JsonResponse<T>> => {
  return fetchJson<T>(path, options).then(({ status, body }) => ({ status, body }));
};

export const baseJsonFetcher = <T>(
  path: string,
  options: BaseJsonFetcherOptions = {},
): Promise<T> => {
  return fetchJson<T>(path, options).then(({ body, parsed, raw, status }) => {
    // Callers of this form treat a non-JSON answer as a failed request, and
    // auth flows branch on the rejection, so it has to stay a rejection.
    if (!parsed) throw new ApiError(`Expected JSON from ${path}`, status, { base: [raw.slice(0, 200)] });

    return body as T;
  });
};

const fetchJson = <T>(
  path: string,
  options: BaseJsonFetcherOptions = {},
): Promise<{ status: number; body: T | undefined; parsed: boolean; raw: string }> => {
  const { clientSlugPath, ...rest } = options;
  const slugPath = clientSlugPath !== undefined ? clientSlugPath : getSlug()
  const endpoint = `${apiUrl(slugPath)}${path}`;
  return fetch(endpoint, mergeOptions(rest))
    .then(async (response) => {
      const raw = await response.text();
      try {
        return { status: response.status, body: camelize(JSON.parse(raw)) as T, parsed: true, raw };
      } catch {
        return { status: response.status, body: undefined, parsed: false, raw };
      }
    });
};

export const swrFetcher = <T>(params: string | [string, FetcherOptions]) => {
  if(typeof params === 'string') return fetcher<T>(params)
    
  return fetcher<T>(params[0], params[1])
}

export const fetcher = <T>(path:string, options:FetcherOptions={}): Promise<FetcherData<T>> => {
  const {queryParams, body, clientSlugPath, ...rest} = options
  const slugPath = clientSlugPath !== undefined ? clientSlugPath : getSlug()

  let endpoint = `${apiUrl(slugPath)}${path}`

  
  const parsedOptions:Options = { 
    ...rest,
    body: typeof body === 'object' ? JSON.stringify(snakeify(body)) : body,
   }

  if(typeof options.queryParams === 'object'){
    endpoint += `?${new URLSearchParams(options.queryParams)}`
  }

  return fetch(endpoint, mergeOptions(parsedOptions))
    .then(async response => {
      if (!response.ok) {
        return handleErrors(response);
      }
      else {
        return response.text()
      }
    })
    .then(text => {
      if (!text) return { data: undefined };
      const textObj = JSON.parse(text)
      return camelize(textObj) as FetcherData<T>
    })
}

// See example here: https://stackoverflow.com/questions/59769649/recursively-convert-an-object-fields-from-snake-case-to-camelcase
const camelize = (obj: any): any => _transform(obj, (acc: any, value, key, target) => {
  const camelKey = _isArray(target) ? key : _camelCase(key.toString());
  acc[camelKey] = _isObject(value) ? camelize(value) : value;
});

const snakeify = (obj: any): any => _transform(obj, (acc: any, value, key, target) => {
  const snakeKey = _isArray(target) ? key : _snakeCase(key.toString());
  acc[snakeKey] = _isObject(value) ? snakeify(value) : value;
})

const extractErrors = (body: string): ApiErrorInfo | null => {
  try {
    const payload = camelize(JSON.parse(body)) as FetcherData<any>;
    if (payload.errors) return payload.errors;
    if (payload.error) return { base: [payload.error] };

    return {};
  } catch {
    return null;
  }
};

const buildErrorMessage = (
  status: number,
  statusText: string,
  url: string,
  errors: ApiErrorInfo | null
): string => {
  const path = new URL(url).pathname;
  const statusPart = statusText || `HTTP ${status}`;

  // Try to extract a meaningful message from errors
  if (errors) {
    if (errors.base?.[0]) {
      return `${statusPart}: ${errors.base[0]} (${path})`;
    }
    const firstKey = Object.keys(errors)[0];
    if (firstKey && errors[firstKey]?.[0]) {
      return `${statusPart}: ${firstKey} ${errors[firstKey][0]} (${path})`;
    }
  }

  return `${statusPart} (${path})`;
};

const handleErrors = async (response: Response) => {
  const body = await response.text();
  const errors = extractErrors(body);
  const errorMessage = buildErrorMessage(
    response.status,
    response.statusText,
    response.url,
    errors
  );

  const error = new ApiError(errorMessage, response.status, errors ?? {});

  // Report 5xx errors and 422 validation errors to Sentry
  const shouldReportToSentry =
    response.status >= 500 || response.status === 422;

  if (shouldReportToSentry) {
    Sentry.captureException(error, {
      tags: {
        api_error: true,
        status_code: response.status,
      },
      extra: {
        url: response.url,
        path: new URL(response.url).pathname,
        errors,
      },
      fingerprint: [
        'api-error',
        String(response.status),
        new URL(response.url).pathname,
      ],
    });
  }

  throw error;
};